import React, { useState } from 'react';
import { Form, Input, Radio, Alert, Space, Typography } from 'antd';
import { trpc } from '../../../../lib/trpc';
import { withDevModalFilePath, NumericInput } from '../../../../components/common';

const { Text } = Typography;

interface CreateCouponModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type DiscountType = 'percent' | 'amount';
type Duration = 'once' | 'forever' | 'repeating';

interface CouponFormValues {
  name: string;
  discountValue: number;
  duration: Duration;
  durationInMonths?: number;
}

const { Modal } = withDevModalFilePath('client/src/pages/admin/subscriptions/components/CreateCouponModal.tsx');

export function CreateCouponModal({ open, onClose, onSuccess }: CreateCouponModalProps) {
  const [form] = Form.useForm();
  const [discountType, setDiscountType] = useState<DiscountType>('percent');
  const [duration, setDuration] = useState<Duration>('repeating');

  const createMutation = trpc.stripeCouponAdmin.createCoupon.useMutation({
    onSuccess: () => {
      form.resetFields();
      onSuccess();
    },
    onError: (error) => {
      console.error('Failed to create coupon:', error);
    },
  });

  const handleSubmit = async (values: CouponFormValues) => {
    await createMutation.mutateAsync({
      name: values.name,
      percentOff: discountType === 'percent' ? values.discountValue : undefined,
      amountOff: discountType === 'amount' ? values.discountValue : undefined,
      currency: discountType === 'amount' ? 'jpy' : undefined,
      duration: values.duration,
      durationInMonths: values.duration === 'repeating' ? values.durationInMonths : undefined,
      metadata: {
        avs_coupon_tag: 'onboarding_discount',
      },
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="新規クーポン作成"
      open={open}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      confirmLoading={createMutation.isPending}
      width={600}
      okText="作成"
      cancelText="キャンセル"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          discountType: 'percent',
          duration: 'repeating',
          durationInMonths: 1,
        }}
      >
        <Alert
          message="注意"
          description="クーポンは作成後、割引率や期間を変更できません。慎重に設定してください。"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item
          name="name"
          label="クーポン名"
          rules={[
            { required: true, message: 'クーポン名を入力してください' },
            { max: 40, message: 'クーポン名は40文字以内で入力してください' },
          ]}
          extra="請求書に表示される名前です"
        >
          <Input placeholder="例: 初期導入割引 50%OFF (1ヶ月)" />
        </Form.Item>

        <Form.Item label="割引タイプ">
          <Radio.Group
            value={discountType}
            onChange={(e) => setDiscountType(e.target.value)}
          >
            <Radio.Button value="percent">パーセント割引</Radio.Button>
            <Radio.Button value="amount">固定金額割引</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="discountValue"
          label={discountType === 'percent' ? '割引率（%）' : '割引額（円）'}
          rules={[
            { required: true, message: '割引値を入力してください' },
            {
              type: 'number',
              min: 1,
              max: discountType === 'percent' ? 100 : undefined,
              message:
                discountType === 'percent'
                  ? '1〜100の範囲で入力してください'
                  : '1以上の値を入力してください',
            },
          ]}
        >
          <NumericInput
            style={{ width: '100%' }}
            min={1}
            max={discountType === 'percent' ? 100 : undefined}
            placeholder={discountType === 'percent' ? '50' : '5000'}
            addonAfter={discountType === 'percent' ? '%' : '円'}
          />
        </Form.Item>

        <Form.Item
          name="duration"
          label="適用期間"
          rules={[{ required: true, message: '適用期間を選択してください' }]}
        >
          <Radio.Group onChange={(e) => setDuration(e.target.value)}>
            <Space direction="vertical">
              <Radio value="once">
                <Space direction="vertical" size={0}>
                  <Text>1回のみ（once）</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    初回請求のみに適用
                  </Text>
                </Space>
              </Radio>
              <Radio value="repeating">
                <Space direction="vertical" size={0}>
                  <Text>繰り返し（repeating）</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    指定した月数分の請求に適用
                  </Text>
                </Space>
              </Radio>
              <Radio value="forever">
                <Space direction="vertical" size={0}>
                  <Text>永続（forever）</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    すべての請求に永続的に適用
                  </Text>
                </Space>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {duration === 'repeating' && (
          <Form.Item
            name="durationInMonths"
            label="適用月数"
            rules={[
              { required: true, message: '適用月数を入力してください' },
              { type: 'number', min: 1, max: 24, message: '1〜24の範囲で入力してください' },
            ]}
          >
            <NumericInput
              style={{ width: '100%' }}
              min={1}
              max={24}
              placeholder="1"
              addonAfter="ヶ月"
            />
          </Form.Item>
        )}

        {createMutation.error && (
          <Alert
            message="エラー"
            description={createMutation.error.message}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}
      </Form>
    </Modal>
  );
}
